import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { firebaseTools } from '@/services/firebase'
import {
  isStreamPath,
  parseForceReloadConfig,
  shouldForceReload,
  shouldShowUpdateBanner,
} from '@/utils/forceReload'

/**
 * Opt-in forced reload of stale clients. How to force one: see utils/forceReload.ts.
 *
 * Subscribes live to RTDB `appConfig` (public read, works before login). A value
 * that requires a reload is kept pending and acted on only at a safe moment:
 *  - app start (the first value) or a return to the app after it was hidden for at
 *    least a minute (a quick tab switch mid-form doesn't count);
 *  - never on the driver stream views or while BackgroundGeolocation is enabled;
 *  - never while a page's beforeunload guard objects.
 * When the auto-reload doesn't happen, `showUpdateBanner` asks the user instead
 * (rendered once in App.vue). It stays hidden on the driver stream views and while
 * native tracking is on; closing it lasts until the next return to the app.
 */

declare const __BUILD_TIME__: number | undefined

const BUILD_TIME = typeof __BUILD_TIME__ === 'number' ? __BUILD_TIME__ : 0
const MIN_HIDDEN_MS = 60 * 1000
// RTDB may deliver the value a few seconds after start or after a Cordova resume
// (it drops the socket on pause), so a start/return stays actionable this long.
const RETURN_WINDOW_MS = 30 * 1000
const ACTED_KEY = 'rs:forceReload:minBuildTime'

function readActedOn(): number | null {
  try {
    const v = Number(sessionStorage.getItem(ACTED_KEY))
    return Number.isFinite(v) && v > 0 ? v : null
  } catch {
    return null
  }
}

function writeActedOn(minBuildTime: number) {
  try {
    sessionStorage.setItem(ACTED_KEY, String(minBuildTime))
  } catch {
    /* private mode: the loop guard is lost, the build check still holds */
  }
}

/** Native tracking outlives the stream view, so ask the plugin itself (Cordova only). */
async function isGeolocationEnabled(): Promise<boolean> {
  const bg = (window as any).BackgroundGeolocation
  if (!bg?.getState) return false
  try {
    const state = await Promise.race([
      bg.getState(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000)),
    ])
    return Boolean((state as any)?.enabled)
  } catch {
    return true // can't tell: treat as tracking
  }
}

/** Fire a cancelable synthetic beforeunload: page guards with unsaved work cancel it. */
function beforeUnloadObjects(): boolean {
  try {
    const probe = new Event('beforeunload', { cancelable: true })
    window.dispatchEvent(probe)
    return probe.defaultPrevented
  } catch {
    return false
  }
}

export function useForceReload() {
  const route = useRoute()
  const updatePending = ref(false)
  const dismissed = ref(false)
  const tracking = ref(false)
  let minBuildTime: number | null = null

  const showUpdateBanner = computed(() =>
    shouldShowUpdateBanner({
      updatePending: updatePending.value,
      dismissed: dismissed.value,
      path: route.path,
      tracking: tracking.value,
    }),
  )

  function dismissUpdateBanner() {
    dismissed.value = true
  }

  /**
   * Banner action. A page guard with unsaved work gets the browser's native confirm;
   * the loop guard is written on pagehide so a cancelled confirm doesn't consume it.
   */
  function reloadForUpdate() {
    const target = minBuildTime
    if (target != null) {
      window.addEventListener('pagehide', () => writeActedOn(target), { once: true })
    }
    window.location.reload()
  }

  const api = { updatePending, showUpdateBanner, dismissUpdateBanner, reloadForUpdate }
  if (typeof window === 'undefined' || !BUILD_TIME) return api

  // App start counts as a return: the first value (if prompt) may reload at once.
  let returnedAt: number | null = Date.now()
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null

  async function isSafeMoment(): Promise<boolean> {
    if (isStreamPath(window.location.pathname)) return false
    if (beforeUnloadObjects()) return false
    return !(await isGeolocationEnabled())
  }

  function isAllowedMoment(): boolean {
    return returnedAt != null && Date.now() - returnedAt < RETURN_WINDOW_MS
  }

  async function tryReload() {
    const target = minBuildTime
    if (target == null || !shouldForceReload(BUILD_TIME, target, readActedOn())) {
      updatePending.value = false
      return
    }
    updatePending.value = true
    if (!isAllowedMoment() || !(await isSafeMoment())) return
    writeActedOn(target)
    window.location.reload()
  }

  // Native tracking can start or stop with any view change: re-check while pending.
  watch(
    [updatePending, () => route.path],
    async ([pending]) => {
      if (pending) tracking.value = await isGeolocationEnabled()
    },
  )

  const unsubscribe = firebaseTools.listen(
    firebaseTools.getRef('appConfig'),
    (snapshot) => {
      minBuildTime = parseForceReloadConfig(snapshot.val())?.minBuildTime ?? null
      tryReload()
    },
  )

  function onHidden() {
    hiddenAt ??= Date.now()
  }

  function onVisible() {
    if (hiddenAt == null) return // already handled (visibilitychange + Cordova resume)
    const longEnough = Date.now() - hiddenAt >= MIN_HIDDEN_MS
    hiddenAt = null
    dismissed.value = false // a closed banner comes back on the next return
    if (!longEnough) return
    returnedAt = Date.now()
    tryReload()
  }

  function onVisibility() {
    if (document.visibilityState === 'hidden') onHidden()
    else onVisible()
  }

  // Cordova's pause/resume back up visibilitychange inside the native shells.
  document.addEventListener('visibilitychange', onVisibility)
  document.addEventListener('pause', onHidden)
  document.addEventListener('resume', onVisible)

  onBeforeUnmount(() => {
    unsubscribe()
    document.removeEventListener('visibilitychange', onVisibility)
    document.removeEventListener('pause', onHidden)
    document.removeEventListener('resume', onVisible)
  })

  return api
}
